import {useEffect, useRef, useMemo, useState} from 'react';
import {Layout, Tabs} from "antd";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {MDBCol, MDBRow} from "mdbreact";
import {collection, getFirestore, query, where} from "firebase/firestore";
import Firebase from "../../Firebase/Firebase";
import {useCollection} from "react-firebase-hooks/firestore";
import {Chart} from "primereact/chart";
import moment from "moment";

const db = getFirestore(Firebase);
const {Content} = Layout;
const {TabPane} = Tabs;

// Capture the mounted element ref BEFORE cleanup fires (React may null the ref during unmount)
function useChart() {
    const ref = useRef<any>(null);
    useEffect(() => {
        const el = ref.current;
        return () => {
            try {
                const instance = el?.getChart?.() ?? el?.chart;
                instance?.destroy();
            } catch (_) {}
        };
    }, []);
    return ref;
}

// Parse amount strings like "5,000" or numeric values
function parseAmount(v: any): number {
    if (v == null) return 0;
    const n = typeof v === 'string' ? parseFloat(v.replace(/,/g, '')) : Number(v);
    return isNaN(n) ? 0 : n;
}

const CHART_OPTS = {
    maintainAspectRatio: false,
    plugins: {legend: {labels: {color: '#495057'}}},
    scales: {
        x: {ticks:{color:'#495057'}, grid:{color:'#ebedef'}},
        y: {ticks:{color:'#495057'}, grid:{color:'#ebedef'}},
    },
};

const PIE_OPTS = {
    maintainAspectRatio: false,
    plugins: {legend: {labels: {color: '#495057'}, position: 'right' as const}},
};

const MONTHS_12 = Array.from({length: 12}, (_, i) =>
    moment().subtract(11 - i, 'months').format('MMM YY')
);
const MONTHS_6 = Array.from({length: 6}, (_, i) =>
    moment().subtract(5 - i, 'months').format('MMM YY')
);

function fmt(n: number) {
    return 'MK ' + n.toLocaleString('en-US', {maximumFractionDigits: 0});
}

const Analytics = () => {
    const [collapsed, setCollapsed] = useState(false);

    const [orderSnap] = useCollection(collection(db, 'orders'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [userSnap] = useCollection(collection(db, 'users'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [productSnap] = useCollection(collection(db, 'products'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [sellerSnap] = useCollection(
        query(collection(db, 'users'), where('type', '==', 'Seller')),
        {snapshotListenOptions: {includeMetadataChanges: true}});

    const orders   = useMemo(() => (orderSnap?.docs   ?? []).map(d => d.data()), [orderSnap]);
    const users    = useMemo(() => (userSnap?.docs    ?? []).map(d => d.data()), [userSnap]);
    const products = useMemo(() => (productSnap?.docs ?? []).map(d => d.data()), [productSnap]);
    const sellers  = useMemo(() => (sellerSnap?.docs  ?? []).map(d => d.data()), [sellerSnap]);

    // ── Revenue & Orders ─────────────────────────────────────────────────────

    const revenueChart = useChart();
    const orderVolumeChart = useChart();

    const revenueData = useMemo(() => {
        // Include all non-cancelled paid orders so chart shows real activity
        const PAID = ['completed','delivered','confirmed','processing','dispatched','paid'];
        const completed = orders.filter(o => PAID.includes((o.orderStatus ?? '').toLowerCase()));
        return {
            labels: MONTHS_12,
            datasets: [{
                label: 'Revenue (MWK)',
                data: MONTHS_12.map(m =>
                    completed
                        .filter(o => {
                            const d = o.createdAt?.toDate ? o.createdAt.toDate() : o.createdAt;
                            return d && moment(d).format('MMM YY') === m;
                        })
                        .reduce((s, o) => s + parseAmount(o.totalAmount), 0)
                ),
                backgroundColor: '#29327b33',
                borderColor: '#29327b',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
            }],
        };
    }, [orders]);

    const orderVolumeData = useMemo(() => {
        const statuses: Record<string, {color: string; border: string}> = {
            completed:  {color: '#22c55e55', border: '#22c55e'},
            pending:    {color: '#f59e0b55', border: '#f59e0b'},
            cancelled:  {color: '#ef444455', border: '#ef4444'},
            delivered:  {color: '#3b82f655', border: '#3b82f6'},
        };
        return {
            labels: MONTHS_6,
            datasets: Object.entries(statuses).map(([status, {color, border}]) => ({
                label: status.charAt(0).toUpperCase() + status.slice(1),
                data: MONTHS_6.map(m =>
                    orders.filter(o => {
                        const d = o.createdAt?.toDate ? o.createdAt.toDate() : o.createdAt;
                        return o.orderStatus === status && d && moment(d).format('MMM YY') === m;
                    }).length
                ),
                backgroundColor: color,
                borderColor: border,
                borderWidth: 2,
            })),
        };
    }, [orders]);

    // ── User Growth ──────────────────────────────────────────────────────────

    const userGrowthChart = useChart();

    const userGrowthData = useMemo(() => ({
        labels: MONTHS_12,
        datasets: [
            {
                label: 'New Buyers',
                data: MONTHS_12.map(m =>
                    users.filter(u => {
                        if (u.type !== 'buyer') return false;
                        const d = u.dateAdded?.toDate ? u.dateAdded.toDate() : u.dateAdded;
                        return d && moment(d).format('MMM YY') === m;
                    }).length
                ),
                fill: false, borderColor: '#42A5F5', tension: 0.4,
            },
            {
                label: 'New Sellers',
                data: MONTHS_12.map(m =>
                    users.filter(u => {
                        if (u.type !== 'Seller') return false;
                        const d = u.dateAdded?.toDate ? u.dateAdded.toDate() : u.dateAdded;
                        return d && moment(d).format('MMM YY') === m;
                    }).length
                ),
                fill: false, borderColor: '#FFA726', tension: 0.4,
            },
        ],
    }), [users]);

    // ── Payment Methods ──────────────────────────────────────────────────────

    const paymentChart = useChart();
    const orderStatusChart = useChart();

    const paymentData = useMemo(() => {
        const counts: Record<string, number> = {};
        orders.forEach(o => {
            const method = o.paymentMethods ?? o.paymentMethod ?? 'Unknown';
            counts[method] = (counts[method] ?? 0) + 1;
        });
        const labels = Object.keys(counts);
        return {
            labels,
            datasets: [{
                data: labels.map(l => counts[l]),
                backgroundColor: ['#42A5F5','#66BB6A','#FFA726','#EF5350','#AB47BC','#26A69A'],
                hoverBackgroundColor: ['#64B5F6','#81C784','#FFB74D','#EF9A9A','#CE93D8','#80CBC4'],
            }],
        };
    }, [orders]);

    const orderStatusData = useMemo(() => {
        const statuses = ['pending','completed','delivered','cancelled','confirmed'];
        const colors   = ['#FFA726','#66BB6A','#42A5F5','#EF5350','#AB47BC'];
        const counts   = statuses.map(s => orders.filter(o => o.orderStatus === s).length);
        return {
            labels: statuses.map(s => s.charAt(0).toUpperCase() + s.slice(1)),
            datasets: [{data: counts, backgroundColor: colors, hoverBackgroundColor: colors}],
        };
    }, [orders]);

    // ── Top Sellers by Revenue ────────────────────────────────────────────────

    const topSellersChart = useChart();

    const topSellersData = useMemo(() => {
        const completed = orders.filter(o =>
            ['completed','delivered','confirmed','processing','dispatched','paid'].includes((o.orderStatus??'').toLowerCase()));
        const revenueMap: Record<string, number> = {};
        completed.forEach(o => {
            const sid = o.sellerId ?? '';
            revenueMap[sid] = (revenueMap[sid] ?? 0) + parseAmount(o.totalAmount);
        });
        const top10 = Object.entries(revenueMap)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10);
        const labels = top10.map(([sid]) => {
            const s = sellers.find(x => x.Uid === sid);
            return s?.fullName ?? s?.shopName ?? sid.slice(0, 8);
        });
        return {
            labels,
            datasets: [{
                label: 'Revenue (MWK)',
                data: top10.map(([, v]) => v),
                backgroundColor: '#29327b88',
                borderColor: '#29327b',
                borderWidth: 2,
            }],
        };
    }, [orders, sellers]);

    // ── Top Products by Units Sold ────────────────────────────────────────────

    const topProductsChart = useChart();

    const topProductsData = useMemo(() => {
        const completed = orders.filter(o =>
            ['completed','delivered','confirmed','processing','dispatched','paid'].includes((o.orderStatus??'').toLowerCase()));
        const unitMap: Record<string, number> = {};
        completed.forEach(o => {
            (o.items ?? []).forEach((item: any) => {
                const pid = item.productId ?? '';
                unitMap[pid] = (unitMap[pid] ?? 0) + (Number(item.quantity) || 1);
            });
        });
        const top10 = Object.entries(unitMap).sort(([,a],[,b]) => b - a).slice(0, 10);
        const labels = top10.map(([pid]) => {
            const p = products.find(x => x.productId === pid);
            return p?.name ?? pid.slice(0, 10);
        });
        return {
            labels,
            datasets: [{
                label: 'Units Sold',
                data: top10.map(([, v]) => v),
                backgroundColor: '#059669aa',
                borderColor: '#059669',
                borderWidth: 2,
            }],
        };
    }, [orders, products]);

    // ── Summary KPIs ─────────────────────────────────────────────────────────

    const completed = orders.filter(o => ['completed','delivered','confirmed','processing','dispatched','paid'].includes((o.orderStatus??'').toLowerCase()));
    const totalRevenue  = completed.reduce((s, o) => s + parseAmount(o.totalAmount), 0);
    const completionRate = orders.length ? ((completed.length / orders.length) * 100).toFixed(1) : '0';
    const cancellations = orders.filter(o => o.orderStatus === 'cancelled').length;

    const kpis = [
        {label:'Total Revenue',        value: fmt(totalRevenue),          color:'#059669'},
        {label:'Total Transactions',   value: String(orders.length),      color:'#0891b2'},
        {label:'Completed Orders',     value: String(completed.length),   color:'#22c55e'},
        {label:'Completion Rate',      value: `${completionRate}%`,       color:'#0284c7'},
        {label:'Cancellations',        value: String(cancellations),      color:'#ef4444'},
        {label:'Active Products',      value: String(products.filter(p => (p.productStatus ?? '').toLowerCase() === 'active').length), color:'#7c3aed'},
        {label:'Total Sellers',        value: String(sellers.length),     color:'#29327b'},
    ];

    return (
        <Layout style={{minHeight:'100vh'}}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <div style={{padding:'20px 24px'}}>
                        <div style={{marginBottom:20}}>
                            <h4 style={{fontWeight:700, fontSize:20, color:'#1a1a2e', margin:0}}>Analytics</h4>
                            <span style={{fontSize:12, color:'#888'}}>Business performance overview</span>
                        </div>

                        {/* KPI row */}
                        <MDBRow className="mb-3">
                            {kpis.map(k => (
                                <MDBCol key={k.label} md="2" style={{paddingLeft:6, paddingRight:6}}>
                                    <div style={{background:'#fff', borderRadius:10, padding:'12px 14px',
                                                 border:'1px solid #eee', borderLeft:`4px solid ${k.color}`,
                                                 boxShadow:'0 1px 4px rgba(0,0,0,.05)', marginBottom:8}}>
                                        <div style={{fontSize:11, color:'#888'}}>{k.label}</div>
                                        <div style={{fontSize:18, fontWeight:800, color:k.color, marginTop:2}}>{k.value}</div>
                                    </div>
                                </MDBCol>
                            ))}
                        </MDBRow>

                        <Tabs defaultActiveKey="revenue" size="large">

                            {/* ── Revenue & Orders ── */}
                            <TabPane tab="Revenue & Orders" key="revenue">
                                <MDBRow>
                                    <MDBCol md="8">
                                        <div style={{background:'#fff', borderRadius:12, padding:20,
                                                     border:'1px solid #eee', marginBottom:16}}>
                                            <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                                Monthly Revenue (past 12 months)
                                            </div>
                                            <div style={{height:280}}>
                                                <Chart ref={revenueChart} type="line" data={revenueData} options={CHART_OPTS}/>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    <MDBCol md="4">
                                        <div style={{background:'#fff', borderRadius:12, padding:20,
                                                     border:'1px solid #eee', marginBottom:16}}>
                                            <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                                Orders by Status
                                            </div>
                                            <div style={{height:280}}>
                                                <Chart ref={orderStatusChart} type="doughnut" data={orderStatusData} options={PIE_OPTS}/>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    <MDBCol md="12">
                                        <div style={{background:'#fff', borderRadius:12, padding:20, border:'1px solid #eee'}}>
                                            <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                                Order Volume by Status (past 6 months)
                                            </div>
                                            <div style={{height:260}}>
                                                <Chart ref={orderVolumeChart} type="bar" data={orderVolumeData} options={CHART_OPTS}/>
                                            </div>
                                        </div>
                                    </MDBCol>
                                </MDBRow>
                            </TabPane>

                            {/* ── User Growth ── */}
                            <TabPane tab="User Growth" key="users">
                                <div style={{background:'#fff', borderRadius:12, padding:20, border:'1px solid #eee'}}>
                                    <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                        New Buyers & Sellers (past 12 months)
                                    </div>
                                    <div style={{height:320}}>
                                        <Chart ref={userGrowthChart} type="line" data={userGrowthData} options={CHART_OPTS}/>
                                    </div>
                                </div>
                            </TabPane>

                            {/* ── Payments ── */}
                            <TabPane tab="Payments" key="payments">
                                <MDBRow>
                                    <MDBCol md="5">
                                        <div style={{background:'#fff', borderRadius:12, padding:20,
                                                     border:'1px solid #eee', marginBottom:16}}>
                                            <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                                Payment Method Breakdown
                                            </div>
                                            <div style={{height:300}}>
                                                <Chart ref={paymentChart} type="pie" data={paymentData} options={PIE_OPTS}/>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    <MDBCol md="7">
                                        <div style={{background:'#fff', borderRadius:12, padding:20, border:'1px solid #eee'}}>
                                            <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                                Payment Breakdown Summary
                                            </div>
                                            {paymentData.labels.map((label, i) => {
                                                const count = paymentData.datasets[0].data[i];
                                                const pct   = orders.length ? ((count / orders.length) * 100).toFixed(1) : '0';
                                                return (
                                                    <div key={label} style={{display:'flex', justifyContent:'space-between',
                                                                             padding:'10px 0', borderBottom:'1px solid #f5f5f5'}}>
                                                        <span style={{fontSize:13, color:'#333'}}>{label}</span>
                                                        <div style={{display:'flex', gap:12}}>
                                                            <span style={{fontSize:13, fontWeight:600, color:'#29327b'}}>{count} orders</span>
                                                            <span style={{fontSize:12, color:'#aaa'}}>{pct}%</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </MDBCol>
                                </MDBRow>
                            </TabPane>

                            {/* ── Top Performers ── */}
                            <TabPane tab="Top Performers" key="top">
                                <MDBRow>
                                    <MDBCol md="6">
                                        <div style={{background:'#fff', borderRadius:12, padding:20,
                                                     border:'1px solid #eee', marginBottom:16}}>
                                            <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                                Top 10 Sellers by Revenue
                                            </div>
                                            <div style={{height:300}}>
                                                <Chart ref={topSellersChart} type="bar"
                                                       data={topSellersData}
                                                       options={{...CHART_OPTS, indexAxis:'y' as const}}/>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    <MDBCol md="6">
                                        <div style={{background:'#fff', borderRadius:12, padding:20, border:'1px solid #eee'}}>
                                            <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:12}}>
                                                Top 10 Products by Units Sold
                                            </div>
                                            <div style={{height:300}}>
                                                <Chart ref={topProductsChart} type="bar"
                                                       data={topProductsData}
                                                       options={{...CHART_OPTS, indexAxis:'y' as const}}/>
                                            </div>
                                        </div>
                                    </MDBCol>
                                </MDBRow>
                            </TabPane>

                        </Tabs>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default Analytics;
